import PropTypes from 'prop-types';
import React from 'react';
import { LinkButton } from '@strava/ui/Button';
import { Markup } from 'interweave';
import I18n from 'utils/I18n';
import { useFeedEntityContext } from '../../../state/FeedEntityContext';
import {
  indexProps,
  cursorDataProps,
  viewingAthleteProps,
  timeAndLocationProps
} from '../../../props/feed-prop-types';
import EntryHeader from '../../shared/EntryHeader/EntryHeader';
import styles from './index.scss';

const PerformanceGoalSingleView = ({
  entity,
  entityId,
  intersectionObserverRef,
  cursorData,
  index,
  viewingAthlete,
  shareable,
  twitterUrl,
  isBoosted,
  timeAndLocation,
  goalEntries
}) => {
  const { entityState } = useFeedEntityContext();
  const cardMargin = index > 0 ? styles.cardMargin : '';
  const goalEntry = goalEntries[0];
  const joinable = viewingAthlete.id !== goalEntry.athlete_id.toString(); // Cast to string to ensure that both are strings
  const entityOwner = {
    displayImage: goalEntry.athlete_avatar_url,
    name: goalEntry.athlete_display_name,
    memberType: goalEntry.athlete_member_type,
    profileUrl: `/athletes/${goalEntry.athlete_id}`,
    type: 'athlete'
  };

  return (
    /* web-feed-4-entry is being used by feed3 container for pagination - https://github.com/strava/active/blob/main/app/assets/javascripts/strava/dashboard/PaginationView.js.coffee#L52-L73 */
    <div
      className={`web-feed-4-entry ${styles.feedEntry} ${styles.card} ${cardMargin}`}
      data-updated-at={cursorData.updated_at}
      data-rank={cursorData.rank}
      data-testid="web-feed-entry"
      ref={intersectionObserverRef}
    >
      <EntryHeader
        entityOwner={entityOwner}
        entityId={`${entityId}`}
        twitterUrl={twitterUrl}
        isBoosted={isBoosted}
        shareable={shareable}
        timeAndLocation={timeAndLocation}
      />
      <div className={styles.entryBody} id={goalEntry.athlete_feed_entry_id}>
        <h3 className={styles.entryTitle} data-testid="goal-description">
          <Markup noWrap={true} content={goalEntry.description} />
        </h3>
        {joinable && (
          <LinkButton
            variant="default"
            className={styles.ctaBtn}
            href={goalEntry.goal_path}
            data-testid="cta-btn"
            onClick={() => {
              entityState.trackAnalytics({
                action: 'click',
                element: `${entity.toLowerCase()}_join_goal`
              });
            }}
          >
            {I18n.t(
              'components.feed.performance_goal_created_single_view.join_goal'
            )}
          </LinkButton>
        )}
      </div>
    </div>
  );
};

PerformanceGoalSingleView.propTypes = {
  entity: PropTypes.oneOf(['PerformanceGoalCreated']).isRequired,
  entityId: PropTypes.number.isRequired,
  intersectionObserverRef: PropTypes.func.isRequired,
  cursorData: cursorDataProps.isRequired,
  index: indexProps.isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  shareable: PropTypes.bool,
  twitterUrl: PropTypes.string.isRequired,
  isBoosted: PropTypes.bool,
  timeAndLocation: timeAndLocationProps.isRequired,
  goalEntries: PropTypes.arrayOf(
    PropTypes.shape({
      shareable: PropTypes.bool,
      is_boosted: PropTypes.bool,
      twitter_url: PropTypes.string.isRequired,
      entity_id: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
      athlete_feed_entry_id: PropTypes.number.isRequired,
      athlete_id: PropTypes.number.isRequired,
      goal_path: PropTypes.string.isRequired,
      athlete_avatar_url: PropTypes.string.isRequired,
      athlete_member_type: PropTypes.string.isRequired,
      athlete_display_name: PropTypes.string.isRequired
    })
  ).isRequired
};

PerformanceGoalSingleView.defaultProps = {
  isBoosted: false,
  shareable: false
};

export default PerformanceGoalSingleView;
